import { addLog } from 'assets/components/feedback/Feedback';
import { getLocales } from 'assets/locales/Locale';
import { date } from 'assets/utils/data/Date';
import { autoTriggerDownload } from 'assets/utils/dom/Download';
import { generateDataUrl } from 'assets/utils/dom/UrlParsing';
import MimeType from 'assets/utils/file/mimeType/MimeType';
import { HttpStatus } from 'config/Api.Config';

/** Every ExportToExcel endpoint returns the workbook as a base64 string. */
type ExcelExportApi<Req> = (body?: Req) => Promise<Api.Res<string>>;

const fileDateFormat = 'DD-MM-YY';

export function generateExcelFileName(name: string): string {
  return `${name}_${date().format(fileDateFormat)}.xlsx`;
}

/** Turns a base64 export response into a browser download and logs the outcome. */
export function downloadExcel(res: Api.Res<string>, name: string): boolean {
  const { lang } = getLocales();

  if (res?.status !== HttpStatus.ok) {
    addLog({ error: res?.message || lang.exportError });
    return false;
  }

  autoTriggerDownload({
    url: generateDataUrl(MimeType.appOPEN_XML_SHEET, res?.payload),
    name: generateExcelFileName(name),
  });
  addLog({ success: lang.exportSuccess });
  return true;
}

/** Calls an export endpoint with an explicit query, then downloads the result. */
export async function exportExcel<Req>(
  excelExportApi: ExcelExportApi<Req>,
  query: Req,
  name: string
): Promise<boolean> {
  const base64 = await excelExportApi(query);

  return downloadExcel(base64, name);
}

/**
 * Calls a list export endpoint with the screen's current filters.
 * Pagination is stripped so the export covers every matching row, not just the visible page.
 */
export async function exportListToExcel<Req extends Api.IDefaultListRequest>(
  excelExportApi: ExcelExportApi<Req>,
  query: Req,
  name: string
): Promise<boolean> {
  const queryReq = { ...(query ?? ({} as Req)) };
  delete queryReq.limit;
  delete queryReq.offset;

  return exportExcel(excelExportApi, queryReq, name);
}
